import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { useMenu } from "@/features/menu/store";
import type {
  CookingStep,
  MenuPlan,
  Recipe,
  RecipeStepsResponse,
} from "@/types/menu";

interface State {
  isLoading: boolean;
  error: string | null;
}

/**
 * Lazily fetches per-recipe Story steps when `recipe.prep_steps` is empty.
 *
 * On success, stashes the steps into the menu store via `setRecipeSteps`
 * so subsequent navigations stay snappy and the data survives reloads
 * via Capacitor Preferences. Fires once per slug (StrictMode-safe).
 */
export function useRecipeSteps(
  recipe: Recipe | null,
  plan: MenuPlan | null,
): State {
  const setRecipeSteps = useMenu((s) => s.setRecipeSteps);
  const [state, setState] = useState<State>({ isLoading: false, error: null });
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!recipe) return;
    if (recipe.prep_steps.length > 0) return;
    if (fetchedFor.current === recipe.slug) return;
    fetchedFor.current = recipe.slug;

    const controller = new AbortController();
    let settled = false;
    setState({ isLoading: true, error: null });
    void api<RecipeStepsResponse>(
      `/api/v1/recipes/${encodeURIComponent(recipe.slug)}/steps`,
      {
        method: "POST",
        body: {
          recipe,
          batch_context: plan,
        },
        signal: controller.signal,
      },
    )
      .then((res) => {
        settled = true;
        setRecipeSteps(recipe.slug, res.steps as CookingStep[]);
        setState({ isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        settled = true;
        // Provider not configured (503) → fall back to a tiny derived
        // placeholder so the page still renders; the user can retry.
        const message =
          err instanceof ApiError
            ? `Erreur ${err.status}: ${
                typeof err.body === "object" && err.body && "detail" in err.body
                  ? String((err.body as { detail: unknown }).detail)
                  : "génération impossible"
              }`
            : (err as Error).message;
        setState({ isLoading: false, error: message });
      });

    return () => {
      controller.abort();
      // An aborted request produced nothing: allow the next effect run to
      // refetch, otherwise the spinner never stops (StrictMode, fast
      // re-render — audit L8).
      if (!settled && fetchedFor.current === recipe.slug) {
        fetchedFor.current = null;
      }
    };
  }, [recipe, plan, setRecipeSteps]);

  return state;
}
